import type { ApprovalStatus, InvoiceStatusUi } from './types'

/**
 * Maps an invoice's approval status into the two-chip UI representation
 * (solid label + subtle sublabel) used in the service-billing list and detail.
 *
 * @param approvalStatus backend approval status
 * @param isSent         whether the invoice has been sent (sentAt present)
 * @param isDraft        whether the invoice is still a draft (status === DRAFT)
 */
export function mapInvoiceStatus(
  approvalStatus: ApprovalStatus,
  isSent = false,
  isDraft = false,
  isCanceled = false
): InvoiceStatusUi | null {
  // A cancellation is terminal and overrides any approval state.
  if (isCanceled) {
    return {
      label: 'Storniert',
      sublabel: '',
      tone: 'danger',
      labelPalette: 'red',
      sublabelPalette: 'red',
    }
  }

  if (approvalStatus === 'PENDING_APPROVAL') {
    return {
      // A draft has not been issued yet; an issued invoice shows "Ausgestellt".
      label: isDraft ? 'Vorschau' : 'Ausgestellt',
      sublabel: 'Wartet auf Freigabe',
      tone: 'warning',
      labelPalette: isDraft ? 'gray' : 'green',
      sublabelPalette: 'yellow',
    }
  }

  if (approvalStatus === 'REJECTED') {
    return {
      label: 'Abgelehnt',
      sublabel: 'Siehe Begründung',
      tone: 'danger',
      labelPalette: 'red',
      sublabelPalette: 'red',
    }
  }

  if (approvalStatus === 'APPROVED' && isSent) {
    return {
      label: 'Freigegeben & ausgestellt',
      sublabel: 'Automatisch versendet',
      tone: 'success',
      labelPalette: 'green',
      sublabelPalette: 'green',
    }
  }

  if (approvalStatus === 'APPROVED') {
    return {
      label: 'Freigegeben',
      sublabel: 'Bereit zum Versand',
      tone: 'success',
      labelPalette: 'green',
      sublabelPalette: 'green',
    }
  }

  // No approval workflow (approvalStatus "NA", e.g. SIB self-billed invoices):
  // reflect the lifecycle status so the badge is never empty.
  if (isDraft) {
    return {
      label: 'Vorschau',
      sublabel: '',
      tone: 'neutral',
      labelPalette: 'gray',
      sublabelPalette: 'gray',
    }
  }

  if (isSent) {
    return {
      label: 'Ausgestellt',
      sublabel: 'Versendet',
      tone: 'success',
      labelPalette: 'green',
      sublabelPalette: 'green',
    }
  }

  return {
    label: 'Ausgestellt',
    sublabel: '',
    tone: 'success',
    labelPalette: 'green',
    sublabelPalette: 'green',
  }
}
